import { customElement, observable } from 'aurelia';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import template from './statistics-view.html';
import { formatFileSize, getStatistics } from '../services/api.service';
import type { PersonProcessingProgressPoint, StatisticsResponse } from '../services/api.types';

Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  TimeScale,
  LineElement,
  LineController,
  PointElement,
);

type ProgressRange = 'all' | 'lastYear' | 'lastMonth' | 'lastWeek';

@customElement({ name: 'statistics-view', template })
export class StatisticsView {
  private typeCanvas!: HTMLCanvasElement;
  private topTagsCanvas!: HTMLCanvasElement;
  private timelineCanvas!: HTMLCanvasElement;
  private processingProgressCanvas!: HTMLCanvasElement;
  private _typeChart: Chart | null = null;
  private _topTagsChart: Chart | null = null;
  private _timelineChart: Chart | null = null;
  private _processingProgressChart: Chart | null = null;
  private _data: StatisticsResponse | null = null;

  public isLoading = true;
  public error = '';

  public readonly progressRanges: { value: ProgressRange; labelKey: string }[] = [
    { value: 'all', labelKey: 'statistics.processingProgress.allTime' },
    { value: 'lastYear', labelKey: 'statistics.processingProgress.lastYear' },
    { value: 'lastMonth', labelKey: 'statistics.processingProgress.lastMonth' },
    { value: 'lastWeek', labelKey: 'statistics.processingProgress.lastWeek' },
  ];

  @observable public progressRange: ProgressRange = 'all';

  public async attached(): Promise<void> {
    try {
      this._data = await getStatistics();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Failed to load statistics.';
    } finally {
      this.isLoading = false;
    }

    if (!this._data || this.error) return;
    requestAnimationFrame(() => {
      if (!this._data) return;
      this.renderTypeChart(this._data);
      this.renderTopTagsChart(this._data);
      this.renderTimelineChart(this._data);
      this.renderProcessingProgressChart();
    });
  }

  public detaching(): void {
    this._typeChart?.destroy();
    this._topTagsChart?.destroy();
    this._timelineChart?.destroy();
    this._processingProgressChart?.destroy();
  }

  public progressRangeChanged(): void {
    this.renderProcessingProgressChart();
  }

  public get totalMediaBytes(): string {
    return formatFileSize(this._data?.totalMediaBytes);
  }

  public get hasProcessingProgressData(): boolean {
    return (this._data?.personProcessingProgress.length ?? 0) > 0;
  }

  public get hasProcessingProgressDataForRange(): boolean {
    const points = this._data?.personProcessingProgress ?? [];
    return this.filterPointsForRange(points, this.progressRange).length > 0;
  }

  private renderTypeChart(data: StatisticsResponse): void {
    this._typeChart = new Chart(this.typeCanvas, {
      type: 'doughnut',
      data: {
        labels: ['Photos', 'Videos'],
        datasets: [{
          data: [data.imageCount, data.videoCount],
          backgroundColor: ['#60a5fa', '#a78bfa'],
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
      },
    });
  }

  private renderTopTagsChart(data: StatisticsResponse): void {
    this._topTagsChart = new Chart(this.topTagsCanvas, {
      type: 'bar',
      data: {
        labels: data.topTags.map(tag => tag.tagName),
        datasets: [{
          data: data.topTags.map(tag => tag.mediaItemCount),
          backgroundColor: data.topTags.map(tag => tag.color || '#6366f1'),
          borderRadius: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  }

  private renderTimelineChart(data: StatisticsResponse): void {
    this._timelineChart = new Chart(this.timelineCanvas, {
      type: 'line',
      data: {
        labels: data.capturedByMonth.map(point => point.label),
        datasets: [{
          label: 'Captured items',
          data: data.capturedByMonth.map(point => point.count),
          borderColor: '#22c55e',
          backgroundColor: 'rgba(34,197,94,0.12)',
          fill: true,
          tension: 0.25,
          pointRadius: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  }

  private renderProcessingProgressChart(): void {
    this._processingProgressChart?.destroy();
    this._processingProgressChart = null;

    const points = this._data?.personProcessingProgress ?? [];
    if (points.length === 0) return;

    const filtered = this.filterPointsForRange(points, this.progressRange);
    if (filtered.length === 0) return;

    const unit = this.chooseTimeUnit(filtered);

    this._processingProgressChart = new Chart(this.processingProgressCanvas, {
      type: 'line',
      data: {
        datasets: [{
          label: 'Processed %',
          data: filtered.map(point => ({ x: new Date(`${point.date}T00:00:00Z`).getTime(), y: point.percentage })),
          borderColor: '#f97316',
          backgroundColor: 'rgba(249,115,22,0.12)',
          fill: true,
          tension: 0.2,
          pointRadius: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            type: 'time',
            time: { unit },
          },
          y: {
            beginAtZero: true,
            max: 100,
            ticks: { callback: value => `${value}%` },
          },
        },
      },
    });
  }

  // Trims the (always all-time, day-granularity) series returned by the backend down to the
  // selected view window. "lastMonth"/"lastYear" clip to that trailing window (clamped to the
  // series' actual start so a young dataset just shows everything it has).
  private filterPointsForRange(points: PersonProcessingProgressPoint[], range: ProgressRange): PersonProcessingProgressPoint[] {
    if (range === 'all') return points;

    const today = new Date();
    const cutoff = new Date(today);
    if (range === 'lastYear') cutoff.setFullYear(cutoff.getFullYear() - 1);
    else if (range === 'lastMonth') cutoff.setMonth(cutoff.getMonth() - 1);
    else cutoff.setDate(cutoff.getDate() - 7);

    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return points.filter(point => point.date >= cutoffStr);
  }

  // Picks a Chart.js time-axis unit so the x-axis stays readable regardless of the span:
  // day-level ticks for short/medium ranges, month-level once it'd otherwise get too crowded,
  // year-level for very long all-time spans.
  private chooseTimeUnit(points: PersonProcessingProgressPoint[]): 'day' | 'month' | 'year' {
    const spanDays = points.length;
    if (spanDays <= 90) return 'day';
    if (spanDays <= 900) return 'month';
    return 'year';
  }
}

